import { cn } from '@/lib/utils';

export interface FormatActions {
  bold: () => void;
  italic: () => void;
  strike: () => void;
  code: () => void;
  codeBlock: () => void;
  spoiler: () => void;
  quote: () => void;
  bullets: () => void;
  numbers: () => void;
  link: () => void;
}

/**
 * The formatting strip above the composer.
 *
 * Shown on demand rather than always: the shortcuts do the same job for
 * people who know them, and a permanent toolbar over a one-line input is
 * mostly chrome. Buttons use `onMouseDown` with `preventDefault` so pressing
 * one never takes focus — and therefore the selection — out of the textarea.
 */
export function FormatToolbar({
  actions,
  className,
}: {
  actions: FormatActions;
  className?: string;
}) {
  const buttons: { label: string; hint: string; render: React.ReactNode; run: () => void }[] = [
    { label: 'Bold', hint: '⌘B', render: <span className="font-bold">B</span>, run: actions.bold },
    {
      label: 'Italic',
      hint: '⌘I',
      render: <span className="font-serif italic">I</span>,
      run: actions.italic,
    },
    {
      label: 'Strikethrough',
      hint: '⌘⇧X',
      render: <span className="line-through">S</span>,
      run: actions.strike,
    },
    {
      label: 'Inline code',
      hint: '⌘E',
      render: <span className="font-mono text-[11px]">{'</>'}</span>,
      run: actions.code,
    },
    {
      label: 'Code block',
      hint: 'Fenced block',
      render: <span className="font-mono text-[10px]">```</span>,
      run: actions.codeBlock,
    },
    {
      label: 'Link',
      hint: '⌘K',
      render: (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path
            d="M9.5 14.5 14.5 9.5M8 12H6.5a3.5 3.5 0 1 1 0-7H10m6 0h1.5a3.5 3.5 0 0 1 0 7H16"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          />
        </svg>
      ),
      run: actions.link,
    },
    { label: 'Quote', hint: 'Block quote', render: <span>&ldquo;</span>, run: actions.quote },
    {
      label: 'Bulleted list',
      hint: 'List',
      render: (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path d="M9 6h11M9 12h11M9 18h11" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          <circle cx="5" cy="6" r="1.3" fill="currentColor" />
          <circle cx="5" cy="12" r="1.3" fill="currentColor" />
          <circle cx="5" cy="18" r="1.3" fill="currentColor" />
        </svg>
      ),
      run: actions.bullets,
    },
    {
      label: 'Numbered list',
      hint: 'Ordered list',
      render: <span className="text-[11px] font-semibold">1.</span>,
      run: actions.numbers,
    },
    {
      label: 'Spoiler',
      hint: 'Hide until clicked',
      render: (
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden>
          <path
            d="M3 12s3.5-6 9-6 9 6 9 6-3.5 6-9 6-9-6-9-6Z"
            stroke="currentColor"
            strokeWidth="1.6"
          />
          <circle cx="12" cy="12" r="2.2" stroke="currentColor" strokeWidth="1.6" />
          <path d="m4 20 16-16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      ),
      run: actions.spoiler,
    },
  ];

  return (
    <div
      className={cn('flex flex-wrap items-center gap-0.5 pb-1.5', className)}
      role="toolbar"
      aria-label="Formatting"
    >
      {buttons.map((button) => (
        <button
          key={button.label}
          type="button"
          aria-label={button.label}
          title={`${button.label} · ${button.hint}`}
          /* Keeps focus and the selection in the textarea. */
          onMouseDown={(event) => event.preventDefault()}
          onClick={button.run}
          className="grid h-7 w-7 place-items-center rounded-md text-slate-400 transition-colors hover:bg-surface-3 hover:text-ink"
        >
          {button.render}
        </button>
      ))}
    </div>
  );
}
